import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Drawer from 'tio-reimagined/components/drawer';
import Button from 'tio-reimagined/components/button';
import { PhFilePdf, PhDownloadSimple, PhCalendar, PhCheckCircle } from 'ember-phosphor-icons';
import ENV from 'tio-employee/config/environment';
import { timeZone } from 'tio-common/utils/date/time-zone';
import { generateRequestId } from 'tio-common/utils/request-id';
import type Owner from '@ember/owner';
import type ReimaginedToastService from 'tio-employee/services/reimagined-toast';
import type SessionContextService from 'tio-employee/services/session-context';

/**
 * PdfExportDrawer — Reimagined PDF export for contribution history
 *
 * Users pick a date range and generate a signed PDF of their
 * employer contribution transactions. Uses the reimagined Drawer
 * with a two-step flow: date selection → download link.
 *
 * @example
 * ```gts
 * <PdfExportDrawer
 *   @isOpen={{this.showPdfDrawer}}
 *   @onClose={{this.closePdfDrawer}}
 *   @employeeId={{this.employeeId}}
 * />
 * ```
 */
interface PdfExportDrawerSignature {
  Args: {
    /** Whether the drawer is open */
    isOpen: boolean;
    /** Called when the drawer should close */
    onClose: () => void;
    /** Employee ID for the API call */
    employeeId: string;
  };
  Element: HTMLDivElement;
}

function getOneYearAgo(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().split('T')[0] as string;
}

function getToday(): string {
  return new Date().toISOString().split('T')[0] as string;
}

export default class PdfExportDrawer extends Component<PdfExportDrawerSignature> {
  @service('reimagined-toast') declare toast: ReimaginedToastService;
  @service declare sessionContext: SessionContextService;

  @tracked startDate = getOneYearAgo();
  @tracked endDate = getToday();
  @tracked pdfUrl = '';
  @tracked pdfFilename = '';

  constructor(owner: Owner, args: PdfExportDrawerSignature['Args']) {
    super(owner, args);
  }

  get hasResult() {
    return this.pdfUrl.length > 0;
  }

  resetState = () => {
    this.startDate = getOneYearAgo();
    this.endDate = getToday();
    this.pdfUrl = '';
    this.pdfFilename = '';
  };

  handleClose = () => {
    this.resetState();
    this.args.onClose();
  };

  updateStartDate = (event: Event) => {
    this.startDate = (event.target as HTMLInputElement).value;
  };

  updateEndDate = (event: Event) => {
    this.endDate = (event.target as HTMLInputElement).value;
  };

  generatePdfTask = dropTask(async () => {
    try {
      const response = await fetch(`${ENV.apiHost}/accounting-transactions/generate_pdf`, {
        method: 'POST',
        body: JSON.stringify({
          employee_id: this.args.employeeId,
          start_date: this.startDate,
          end_date: this.endDate,
        }),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ENV.apiKey,
          'tio-auth-token': this.sessionContext.session.data.authenticated?.access_token || '',
          'Time-Zone': timeZone(),
          'X-Request-Id': generateRequestId(),
        },
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        console.error('Failed to generate transactions PDF:', errorPayload || response.statusText);
        throw new Error('PDF generation failed');
      }

      const payload = await response.json();
      const signedUrl = payload.url as string;
      const fileName = payload.filename as string;

      if (!signedUrl || !fileName) {
        throw new Error('Missing signed URL or file name in response');
      }

      this.pdfUrl = signedUrl;
      this.pdfFilename = fileName;

      this.toast.success('PDF generated successfully.');
    } catch (error) {
      console.error('Error during PDF generation:', error);
      this.toast.error('Failed to generate PDF. Please try again.');
    }
  });

  <template>
    {{! template-lint-disable no-bare-strings }}
    <Drawer
      @isOpen={{@isOpen}}
      @onClose={{this.handleClose}}
      @size="lg"
      @placement="right"
      ...attributes
    >
      <:header>
        <div class="flex items-center gap-3">
          <div class="p-2 bg-violet-100 rounded-lg">
            <PhFilePdf @size={{20}} class="text-violet-600" />
          </div>
          <div>
            <Heading @level={{3}} class="text-lg font-semibold text-gray-900">
              Export Contributions
            </Heading>
            <Text class="text-sm text-gray-500">
              Generate a PDF of your contribution history
            </Text>
          </div>
        </div>
      </:header>
      <:body>
        {{#if this.hasResult}}
          {{! Success state — show download link }}
          <div class="flex flex-col items-center text-center py-8 space-y-6">
            <div class="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <PhCheckCircle @size={{32}} class="text-emerald-600" />
            </div>
            <div>
              <Heading @level={{4}} class="text-lg font-semibold text-gray-900 mb-1">
                PDF Ready
              </Heading>
              <Text class="text-gray-500">
                Your contribution report has been generated.
              </Text>
            </div>
            <a
              href={{this.pdfUrl}}
              download={{this.pdfFilename}}
              class="inline-flex items-center gap-2 px-6 py-3 bg-violet-600 text-white rounded-xl font-medium hover:bg-violet-700 transition-colors shadow-sm"
            >
              <PhDownloadSimple @size={{20}} />
              Download PDF
            </a>
            <Text class="text-xs text-gray-400">
              {{this.pdfFilename}}
            </Text>
          </div>
        {{else}}
          {{! Date range selection }}
          <div class="space-y-6">
            <div class="bg-violet-50 rounded-xl p-4 border border-violet-100">
              <div class="flex items-start gap-3">
                <PhCalendar @size={{20}} class="text-violet-600 mt-0.5" />
                <div>
                  <Text class="font-medium text-gray-900 mb-1">Select Date Range</Text>
                  <Text class="text-sm text-gray-600">
                    Choose the period for your contribution report. The PDF will include all
                    employer payments within this range.
                  </Text>
                </div>
              </div>
            </div>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1.5" for="pdf-start-date">
                  Start Date
                </label>
                <input
                  type="date"
                  id="pdf-start-date"
                  value={{this.startDate}}
                  {{on "input" this.updateStartDate}}
                  class="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-gray-900 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1.5" for="pdf-end-date">
                  End Date
                </label>
                <input
                  type="date"
                  id="pdf-end-date"
                  value={{this.endDate}}
                  {{on "input" this.updateEndDate}}
                  class="w-full px-3 py-2.5 rounded-xl border border-gray-300 text-gray-900 focus:ring-2 focus:ring-violet-500 focus:border-violet-500 transition-colors"
                />
              </div>
            </div>
          </div>
        {{/if}}
      </:body>
      <:footer>
        <div class="flex justify-end gap-3">
          {{#if this.hasResult}}
            <Button @appearance="outlined" {{on "click" this.resetState}}>
              New Export
            </Button>
            <Button @intent="primary" {{on "click" this.handleClose}}>
              Done
            </Button>
          {{else}}
            <Button @appearance="outlined" {{on "click" this.handleClose}}>
              Cancel
            </Button>
            <Button
              @intent="primary"
              @isRunning={{this.generatePdfTask.isRunning}}
              @icon={{PhFilePdf}}
              {{on "click" this.generatePdfTask.perform}}
            >
              Generate PDF
            </Button>
          {{/if}}
        </div>
      </:footer>
    </Drawer>
  </template>
}
